import type { Direction, Shadows, Theme, ThemeOptions } from '@mui/material';
import { filledInputClasses } from '@mui/material/FilledInput';
import { createTheme as createMuiTheme, responsiveFontSizes } from '@mui/material/styles';

import type { Typography } from './types';

export const sidebarWidth = '256px';

export const advisorSideBarWidth = '320px';

export const inputTextHeight = '1.4375em';

// Colors — CSS vars resolved from packages/savvy-kit/src/styles/globals.css.
// Each token has a `:root` (light) and `.dark` (dark) definition, so web consumers
// using `neutral[200]` etc. via MUI `sx` or inline styles automatically flip with
// the theme. React Native cannot parse CSS variables — native consumers must
// import the `*Hex` mirrors below instead.
export const neutral = {
  0: 'var(--savvy-neutral-0)',
  50: 'var(--savvy-neutral-50)',
  100: 'var(--savvy-neutral-100)',
  200: 'var(--savvy-neutral-200)',
  300: 'var(--savvy-neutral-300)',
  400: 'var(--savvy-neutral-400)',
  500: 'var(--savvy-neutral-500)',
  600: 'var(--savvy-neutral-600)',
  700: 'var(--savvy-neutral-700)',
  800: 'var(--savvy-neutral-800)',
  900: 'var(--savvy-neutral-900)',
};

// Native-safe hex mirror of `neutral`. Use ONLY in React Native (`apps/mobile-client`)
// — these values do not flip with the web `.dark` class.
export const neutralHex = {
  0: '#FFFFFF',
  50: '#FAFBFB',
  100: '#F4F5F6',
  200: '#E8EAED',
  300: '#D1D5DB',
  400: '#9EA6B3',
  500: '#848C9A',
  600: '#6A7281',
  700: '#515767',
  800: '#383D4D',
  900: '#1D2334',
};

// CSS-var versions: use these in `sx`/inline styles so they auto-flip with the `.dark`
// class. Do NOT pass these to MUI `palette.*` slots — MUI runs `alpha()`/`darken()` on
// palette values and colorManipulator cannot parse `var(...)`.
export const backgroundColors = {
  contrast: 'var(--savvy-background-contrast)',
  default: 'var(--savvy-background-default)',
  info: 'var(--savvy-background-info)',
  light: 'var(--savvy-background-light)',
  alert: 'var(--savvy-background-alert)',
};

// Native-safe hex mirror of `backgroundColors`.
export const backgroundColorsHex = {
  contrast: '#FAFBFB',
  default: '#FFFFFF',
  info: '#F6FBFE',
  light: '#F9F9FE',
  alert: '#F5F5F5',
};

export const dividerColor = 'var(--savvy-divider)';

// Hex aliases for MUI `palette.*` slots — colorManipulator can't parse var(...).
// createTheme() overrides these in dark mode via darkBackgroundColors / darkDividerColor.
const paletteBackgroundColors = backgroundColorsHex;
const paletteDividerColor = '#E8EAED';

export const primaryColors = {
  main: '#175242',
  light: '#457568',
  dark: '#0E3128',
  gold: '#8E7E57',
  ecru: '#C7BCA1',
  vanilla: '#FFF8F1',
  contrastText: '#FFFFFF',
};

export const financialAccountIconColors = {
  bankAccount: '#9078C8',
  companyEquity: '#6FCCDF',
  investmentAccount: '#B06FC4',
  otherAssets: '#67B3AC',
  liability: '#D97C75',
  realEstateAsset: '#6BC0F4',
};

export const greyIconsColor = '#6B7280';

export { assetCategoryColors } from './asset-category-colors';

export const graphColors = {
  linearGradient: 'var(--graph-area-total)',
  primaryLinearGradientTop: '#B1C4BD',
  primaryDateRangeOptionsBackground: '#D8DFDD',
  totalValue: 'var(--graph-line-total)',
  netInvested: '#2C9075',
  tooltipBackground: '#1752421A',
  accountGradient: '#6B484D33',
  accountTotalValue: '#6B484D',
  accountNetInvested: '#C06F74',
  accountTooltipBackground: '#6B484D0D',
  accountTooltipBackgroundOpaque: '#6B484D',
  grayGrid: 'var(--graph-grid)',
};

export const mobileToggleColors = {
  netWorth: {
    text: neutral[0],
    active: '#346053',
    background: '#4F7B6F',
  },
  portfolio: {
    text: '#0E392E',
    active: '#D7E0DC',
    background: '#EDF1EE',
  },
  account: {
    text: '#6B484D',
    active: '#EBE4E5',
    background: '#F9F6F7',
  },
};

export const secondaryColors = {
  main: '#10B981',
  mainLowOpacity: 'rgba(16, 185, 129, 0.08)',
  light: '#3FC79A',
  dark: '#0B815A',
  contrastText: '#FFFFFF',
};

export const successColors = {
  main: '#14B8A6',
  light: '#43C6B7',
  dark: '#0E8074',
  contrastText: '#FFFFFF',
  lightGreen: 'var(--savvy-success-light-green)',
  darkGreen: 'var(--savvy-success-dark-green)',
  alertContent: 'var(--savvy-success-alert-content)',
  alertBackground: 'var(--savvy-success-alert-background)',
};

// Native-safe hex mirror of `successColors`. Use ONLY in React Native (`apps/mobile-client`
// and shared `.native.tsx` / RN-using files) — these values do not flip with the web `.dark` class.
export const successColorsHex = {
  main: '#14B8A6',
  light: '#43C6B7',
  dark: '#0E8074',
  contrastText: '#FFFFFF',
  lightGreen: '#fafefb',
  darkGreen: '#10b981',
  alertContent: '#084a42',
  alertBackground: '#e7f8f6',
};

export const infoColors = {
  main: '#2196F3',
  light: '#64B6F7',
  dark: '#0B79D0',
  contrastText: '#FFFFFF',
  alertContent: 'var(--savvy-info-alert-content)',
  alertBackground: 'var(--savvy-info-alert-background)',
};

export const warningColors = {
  main: '#FFB020',
  light: '#FFBF4C',
  dark: '#B27B16',
  lightYellow: 'var(--savvy-warning-alert-background)',
  darkYellow: 'var(--savvy-warning-alert-content)',
  contrastText: '#FFFFFF',
  alertBackground: 'linear-gradient(0deg, rgba(255, 255, 255, 0.90) 0%, rgba(255, 255, 255, 0.90) 100%), #FFB020',
};

export const errorColors = {
  main: '#D14343',
  light: '#DA6868',
  // dark stays hex: MUI's colorManipulator (darken/alpha) runs on theme.palette.error.dark
  // and cannot parse var(...). For UI text/icon colors that should flip in dark mode,
  // use `alertContent` below instead.
  dark: '#922E2E',
  contrastText: '#FFFFFF',
  background: 'var(--savvy-error-alert-background)',
  alertBackground: 'var(--savvy-error-alert-background)',
  alertContent: 'var(--savvy-error-alert-content)',
};

// CSS-var versions: ~180 consumer files use these in `sx`/inline styles. The vars
// resolve via `:root` (light) and `.dark` (dark) so the same call sites flip without
// per-file edits. Do NOT feed these to MUI palette slots — MUI's Button/Chip/Skeleton/
// ToggleButton run alpha() on palette.text.* and colorManipulator cannot parse var(...).
export const textColors = {
  primary: 'var(--savvy-neutral-900)',
  charcoal: 'var(--savvy-neutral-800)',
  secondary: 'var(--savvy-neutral-600)',
  disabled: 'var(--savvy-text-disabled)',
};

// Native-safe hex mirror of `textColors`.
export const textColorsHex = {
  primary: '#1D2334',
  charcoal: '#383D4D',
  secondary: '#6A7281',
  disabled: 'rgba(132, 140, 154, 0.5)',
};

const paletteTextColors = textColorsHex;

export const goalsColors = {
  on_track: '#64B6F7',
  off_track: textColors.primary,
  off_track_tooltip: neutral[300],
  at_risk: '#FFB020',
  completed: textColors.secondary,
};

export const financialOutlookColors = {
  poor: errorColors.dark,
  hover_poor: '#f7e7e7',
  not_good: errorColors.light,
  hover_not_good: '#faecec',
  neutral: neutral[400],
  hover_neutral: '#f5f6f7',
  good: secondaryColors.main,
  hover_good: '#e3fdf4',
  great: secondaryColors.dark,
  hover_great: '#ddfcf2',
};

export const globalCreateColors = {
  lightGreen: '#E5F6FA',
  darkGreen: '#38818D',
  lightPurple: '#E9EAF5',
  darkPurple: '#2A358E',
  lightBlue: '#0C7CD5',
  darkBlue: '#284963',
};

export const advisorLoginAsBannerColor = '#EDF8FF'; // Light blue for advisor login as banner
export const adminLoginAsBannerColor = '#E8F5E9'; // Light mint green for admin login as banner

export const actionColors = {
  // MUI internally runs alpha() on action.active for hover/focus states, so this
  // must stay a hex — CSS vars break colorManipulator. Low-opacity overlays look
  // fine on both light and dark backgrounds.
  active: '#848C9A',
  focus: 'rgba(55, 65, 81, 0.12)',
  hover: 'rgba(55, 65, 81, 0.04)',
  selected: 'rgba(55, 65, 81, 0.08)',
  disabledBackground: 'rgba(55, 65, 81, 0.12)',
  disabled: 'rgba(55, 65, 81, 0.26)',
};

export const aiColors = {
  background: '#F1F2FF',
  stroke: '#B9BDDF',
  text: '#4655CE',
  textDark: '#0044C8',
};

export const marketingColors = {
  hover: '#00000026',
  menuHover: '#1D23340F',
  maroon: '#6B484D',
  deepBlue: '#095972',
  goldenYellow: '#D79F32',
  marketingRequestYellow: '#FFB547',
  marketingRequestBlue: '#2F3EB1',
};

export const strategyColors = {
  firstParty: 'var(--savvy-strategy-first-party)',
  thirdParty: 'var(--savvy-strategy-third-party)',
};

export const advisorDashNavCssVars = {
  '--nav-bg': backgroundColors.default,
  '--nav-color': textColors.primary,
  '--nav-divider-color': dividerColor,
  '--nav-border-color': neutral[100],
  '--nav-logo-border': neutral[100],
  '--nav-section-title-color': neutral[400],
  '--nav-item-color': textColors.secondary,
  '--nav-item-hover-bg': actionColors.hover,
  '--nav-item-active-bg': actionColors.selected,
  '--nav-item-active-color': textColors.primary,
  '--nav-item-disabled-color': neutral[400],
  '--nav-item-icon-color': neutral[400],
  '--nav-item-icon-active-color': primaryColors.main,
  '--nav-item-icon-disabled-color': neutral[400],
  '--nav-item-chevron-color': neutral[400],
  '--nav-scrollbar-color': neutral[900],
};

export const shadows: Shadows = [
  'none',
  '0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)',
  '0px 1px 2px rgba(100, 116, 139, 0.12)',
  '0px 1px 4px rgba(100, 116, 139, 0.12)',
  '0px 1px 5px rgba(100, 116, 139, 0.12)',
  '0px 1px 6px rgba(100, 116, 139, 0.12)',
  '0px 2px 6px rgba(100, 116, 139, 0.12)',
  '0px 3px 6px rgba(100, 116, 139, 0.12)',
  '0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)',
  '0px 5px 12px rgba(100, 116, 139, 0.12)',
  '0px 5px 14px rgba(100, 116, 139, 0.12)',
  '0px 5px 15px rgba(100, 116, 139, 0.12)',
  '0px 6px 15px rgba(100, 116, 139, 0.12)',
  '0px 7px 15px rgba(100, 116, 139, 0.12)',
  '0px 8px 15px rgba(100, 116, 139, 0.12)',
  '0px 9px 15px rgba(100, 116, 139, 0.12)',
  '0px 10px 15px rgba(100, 116, 139, 0.12)',
  '0px 12px 22px -8px rgba(100, 116, 139, 0.25)',
  '0px 13px 22px -8px rgba(100, 116, 139, 0.25)',
  '0px 14px 24px -8px rgba(100, 116, 139, 0.25)',
  '0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
  '0px 25px 50px rgba(100, 116, 139, 0.25)',
];

export const filledInputStyleOverrides = {
  root: {
    borderColor: neutral[200],
    backgroundColor: 'transparent' as const,
    borderRadius: 8,
    borderStyle: 'solid' as const,
    borderWidth: 1,
    overflow: 'hidden' as const,
    // MUI default for transitions.create(['border-color', 'box-shadow']), inlined so this shared
    // module builds no theme at import: MUI's createTheme is a "use client" export and throws when
    // a server (App Router) module evaluates it — e.g. the scheduled-report generation route.
    transition: 'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: actionColors.hover,
    },
    [`&.${filledInputClasses.disabled}`]: {
      backgroundColor: 'transparent',
    },
    [`&.${filledInputClasses.focused}`]: {
      backgroundColor: 'transparent',
      borderColor: primaryColors.main,
      boxShadow: `${primaryColors.main} 0 0 0 1px`,
    },
    [`&.${filledInputClasses.error}`]: {
      borderColor: errorColors.main,
    },
    [`&.${filledInputClasses.error}.${filledInputClasses.focused}`]: {
      boxShadow: `${errorColors.main} 0 0 0 1px`,
    },
    [`&.${filledInputClasses.inputHiddenLabel}`]: {
      paddingTop: '4px',
    },
    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },
  },
};

export const buttonStyleOverrides = {
  root: {
    textTransform: 'none',
    borderRadius: '8px',
  },
  sizeSmall: {
    paddingBottom: 6,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 6,
    borderRadius: 8,
  },
  sizeMedium: {
    paddingBottom: 8,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 8,
    borderRadius: 8,
  },
  sizeLarge: {
    paddingBottom: 11,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 11,
    borderRadius: 8,
  },
  textSizeSmall: {
    padding: '7px 12px',
  },
  textSizeMedium: {
    padding: '9px 16px',
  },
  textSizeLarge: {
    padding: '12px 16px',
  },
  iconSizeSmall: {
    padding: '6px 4px',
  },
  // `neutral[900]` is the CSS-var token that resolves to #1D2334 in light and #FAFAFA
  // in dark (same light-mode value as textColors.primary, so no visual change in light).
  // Using it here instead of the hardcoded hex makes the button text legible in dark mode.
  containedSecondary: {
    color: neutral[900],
    border: `1px solid ${neutral[300]}`,
    '&:hover': {
      backgroundColor: backgroundColors.contrast,
      borderColor: neutral[300],
    },
  },
  textSecondary: {
    color: neutral[900],
    '&:hover': {
      backgroundColor: backgroundColors.contrast,
    },
  },
  outlinedSecondary: {
    color: neutral[900],
    border: `1px solid ${neutral[300]}`,
    '&:hover': {
      backgroundColor: backgroundColors.contrast,
      borderColor: neutral[300],
    },
  },
};

export const typography: Typography = {
  fontFamily:
    '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
  fontWeightBold: 600,
  button: {
    fontWeight: '600',
  },
  body1: {
    fontSize: '1rem',
    fontWeight: '400',
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: '400',
    lineHeight: 1.57,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: '600',
    lineHeight: 1.75,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: '600',
    lineHeight: 1.57,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    lineHeight: 2.5,
    textTransform: 'uppercase',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: '400',
    lineHeight: 1.66,
  },
  h1: {
    fontWeight: '600',
    fontSize: '3.5rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
  h2: {
    fontWeight: '600',
    fontSize: '3rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
  h3: {
    fontWeight: '600',
    fontSize: '2.25rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
  h4: {
    fontWeight: '600',
    fontSize: '2rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
  h5: {
    fontWeight: '600',
    fontSize: '1.5rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
  h6: {
    fontWeight: '600',
    fontSize: '1.125rem',
    letterSpacing: '-0.02em',
    lineHeight: 1.375,
  },
};

export const themeOptions: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          '&.MuiAutocomplete-option[aria-selected="true"]: hover': {
            background: actionColors.hover,
          },
          '&.MuiAutocomplete-option[aria-selected="true"]': {
            background: 'transparent',
          },
          '&.MuiAutocomplete-option.Mui-focusVisible': {
            background: actionColors.hover,
          },
          '&.MuiAutocomplete-option.Mui-focusVisible[aria-selected="true"]': {
            background: actionColors.hover,
          },
          '&.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
            background: actionColors.hover,
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[500],
          color: '#FFFFFF',
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          marginLeft: '4px',
          marginRight: '4px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
      },
    },
    MuiFilledInput: {
      styleOverrides: filledInputStyleOverrides,
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: buttonStyleOverrides,
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '32px 24px',
          '&:last-child': {
            paddingBottom: '32px',
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
        subheaderTypographyProps: {
          variant: 'body2',
        },
      },
      styleOverrides: {
        root: {
          padding: '32px 24px',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          scrollBehavior: 'smooth',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
          overflowY: 'scroll',
        },
        body: {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          minHeight: '100%',
          width: '100%',
        },
        '#__next': {
          display: 'flex',
          flex: '1 1 auto',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
        },
        '#nprogress': {
          pointerEvents: 'none',
        },
        '#nprogress .bar': {
          backgroundColor: primaryColors.main,
          height: 3,
          left: 0,
          position: 'fixed',
          top: 0,
          width: '100%',
          zIndex: 2000,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '32px',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: '16px',
          '&.MuiListItemIcon-root': {
            minWidth: 'unset',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          '& fieldset': {
            borderRadius: '8px',
          },
        },
        input: {
          fontWeight: 500,
        },
        notchedOutline: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 16,
      },
      styleOverrides: {
        paper: ({ theme }) => ({
          borderColor: theme.palette.divider,
          borderStyle: 'solid',
          borderWidth: 1,
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'primary',
      },
      styleOverrides: {
        switchBase: {
          color: neutral[500],
        },
        track: {
          backgroundColor: neutral[400],
          opacity: 1,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.divider,
          borderStyle: 'solid',
          borderWidth: 1,
          borderRadius: '4px',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: textColors.charcoal,
          ':hover': {
            color: textColors.charcoal,
            background: 'rgba(0, 48, 212, 0.08)',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            color: primaryColors.contrastText,
            background: primaryColors.main,
          },
          borderRadius: '8px',
        },
        sizeLarge: {
          paddingTop: '22px',
          paddingBottom: '22px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '15px 16px',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiTableRow-hover': {
            cursor: 'pointer',
            ':hover': {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06) !important'
                  : 'rgba(72, 109, 229, 0.08) !important',
            },
          },
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: 'none',
          '& .MuiTableCell-root': {
            borderBottom: 'none',
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : neutral[700],
          },
          '& .MuiTableCell-paddingCheckbox': {
            paddingTop: 4,
            paddingBottom: 4,
          },
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : neutral[100],
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: 'solid',
          borderRadius: 8,
          borderWidth: 1,
          borderColor: theme.palette.divider,
          padding: theme.spacing(2),
          boxShadow:
            theme.palette.mode === 'dark' ? '0px 1px 6px rgba(0, 0, 0, 0.5)' : '0px 1px 6px rgba(100, 116, 139, 0.12)',
          ...typography.body2,
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.background.paper,
          '&::before': {
            border: 'solid',
            borderWidth: 1,
            borderColor: theme.palette.divider,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          '&.MuiChip-filledDefault': {
            backgroundColor: neutral[200],
            '& .MuiChip-deleteIcon': {
              color: neutral[400],
            },
            '& .MuiChip-deleteIcon:hover': {
              color: neutral[600],
            },
          },
          '&.MuiChip-outlinedDefault': {
            borderColor: neutral[200],
            '& .MuiChip-deleteIcon': {
              color: neutral[300],
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: textColors.secondary,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderColor: dividerColor,
          borderStyle: 'solid',
          borderWidth: 1,
        },
      },
    },
  },
  palette: {
    action: actionColors,
    background: paletteBackgroundColors,
    divider: paletteDividerColor,
    error: errorColors,
    info: infoColors,
    mode: 'light',
    // @ts-expect-error - Mui v5 doesn't recognize the neutral object as a valid palette color
    neutral,
    primary: primaryColors,
    secondary: secondaryColors,
    success: successColors,
    text: paletteTextColors,
    warning: warningColors,
  },
  shadows,
  direction: 'ltr',
  typography,
  zIndex: {
    appBar: 1200,
    drawer: 1100,
  },
};

type ThemeConfig = {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  mode?: 'light' | 'dark';
};

const darkBackgroundColors = {
  contrast: '#0A0A0B',
  default: '#0A0A0B',
  paper: '#18181B',
  info: '#0B2432',
  light: '#131316',
  alert: '#1F1F23',
};

const darkTextColors = {
  primary: '#F4F4F5',
  charcoal: '#E4E4E7',
  secondary: '#A1A1AA',
  disabled: 'rgba(161, 161, 170, 0.5)',
};

const darkDividerColor = 'rgba(255, 255, 255, 0.1)';

export function createTheme(config: ThemeConfig): Theme {
  const isDark = config.mode === 'dark';

  const overrides = isDark
    ? {
        direction: config.direction,
        palette: {
          mode: 'dark' as const,
          background: darkBackgroundColors,
          divider: darkDividerColor,
          text: darkTextColors,
          primary: {
            ...primaryColors,
            main: primaryColors.light,
            dark: primaryColors.main,
            light: '#6F9A8E',
          },
          // In light mode, actionColors uses dark-tinted rgba that works on white
          // backgrounds. In dark mode we need white-tinted rgba so disabled states
          // (buttons, inputs) are visible on near-black surfaces.
          action: {
            active: 'rgba(255, 255, 255, 0.56)',
            hover: 'rgba(255, 255, 255, 0.08)',
            selected: 'rgba(255, 255, 255, 0.16)',
            disabled: 'rgba(255, 255, 255, 0.3)',
            disabledBackground: 'rgba(255, 255, 255, 0.12)',
            focus: 'rgba(255, 255, 255, 0.12)',
          },
        },
      }
    : {
        direction: config.direction,
      };

  let theme = createMuiTheme(themeOptions, overrides);

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
}

// Native theme mirror. Intentionally co-located with its web source here in
// deprecated-shared (rather than a new package) despite the no-new-code rule:
// these exports must track `themeOptions` / `filledInputStyleOverrides` above
// value-for-value, and splitting them into a separate package would let the two
// drift silently. Revisit if/when the theme itself moves out of deprecated-shared.
//
// CSS variables -> hex map for React Native. Source: packages/web-ui/src/styles/globals.css
// (light-mode :root block). React Native cannot resolve `var(--foo)` color strings, so any
// theme value that flows into a native style needs the var swapped out at module-load time.
const CSS_VAR_HEX_MAP: Record<string, string> = {
  '--savvy-neutral-0': '#ffffff',
  '--savvy-neutral-50': '#fafbfb',
  '--savvy-neutral-100': '#f4f5f6',
  '--savvy-neutral-200': '#e8eaed',
  '--savvy-neutral-300': '#d1d5db',
  '--savvy-neutral-400': '#9ea6b3',
  '--savvy-neutral-500': '#848c9a',
  '--savvy-neutral-600': '#6a7281',
  '--savvy-neutral-700': '#515767',
  '--savvy-neutral-800': '#383d4d',
  '--savvy-neutral-900': '#1d2334',
  '--savvy-background-default': '#ffffff',
  '--savvy-background-contrast': '#fafbfb',
  '--savvy-background-info': '#f6fbfe',
  '--savvy-background-light': '#f9f9fe',
  '--savvy-background-alert': '#f5f5f5',
  '--savvy-divider': '#e8eaed',
  '--savvy-success-alert-content': '#084a42',
  '--savvy-success-alert-background': '#e7f8f6',
  '--savvy-success-dark-green': '#10b981',
  '--savvy-success-light-green': '#fafefb',
  '--savvy-warning-alert-background': '#fff3e0',
  '--savvy-warning-alert-content': '#665233',
  '--savvy-error-alert-background': '#faecec',
  '--savvy-error-alert-content': '#922e2e',
  '--savvy-info-alert-background': '#eff8fe',
  '--savvy-info-alert-content': '#284963',
  '--savvy-strategy-first-party': '#e5f4fd',
  '--savvy-strategy-third-party': '#ece7f5',
  '--savvy-text-disabled': 'rgba(132, 140, 154, 0.5)',
  '--graph-line-total': '#175242',
  '--graph-area-total': '#1752424d',
  '--graph-grid': '#e8eaed',
};

/* eslint-disable typescript/consistent-type-assertions -- generic recursive runtime resolver over unknown-shaped values; the as-unknown-as-T casts are inherent to preserving the caller's T through the transform */
function resolveCssVarsForNative<T>(value: T): T {
  if (typeof value === 'string') {
    if (!value.includes('var(--')) return value;
    return value.replace(/var\(--([\w-]+)\)/g, (match, name) => {
      const hex = CSS_VAR_HEX_MAP[`--${name}`];
      return hex ?? match;
    }) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map(resolveCssVarsForNative) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      out[key] = resolveCssVarsForNative((value as Record<string, unknown>)[key]);
    }
    return out as unknown as T;
  }
  return value;
}
/* eslint-enable typescript/consistent-type-assertions */

export const themeOptionsNative: ThemeOptions = resolveCssVarsForNative(themeOptions);

// React Native-safe mirrors for exports that contain CSS-var strings and are imported
// directly (not via `useTheme` / `themeOptionsNative.*`) by `.native.tsx` components.
export const filledInputStyleOverridesNative = resolveCssVarsForNative(filledInputStyleOverrides);
